export interface IExcelNumberCell {
  __excelNumber: true;
  value: number;
}

/**
 * Marca um valor para ser exportado como célula numérica no Excel.
 * Retorna null quando o valor não é um número válido, permitindo
 * o fallback para um placeholder textual (ex.: ' - ').
 */
export const excelNumberCell = (
  value: string | number | null | undefined
): IExcelNumberCell | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? { __excelNumber: true, value } : null;
  }

  const text = value.trim().replace(/\s/g, '');
  if (!/^[+-]?(?:\d[\d.,]*|[.,]\d+)$/.test(text)) {
    return null;
  }

  const sign = text.startsWith('-') || text.startsWith('+') ? text[0] : '';
  const unsigned = sign ? text.slice(1) : text;
  const lastComma = unsigned.lastIndexOf(',');
  const lastDot = unsigned.lastIndexOf('.');
  const hasComma = lastComma >= 0;
  const hasDot = lastDot >= 0;
  let decimalSeparator: string | undefined;

  if (hasComma && hasDot) {
    decimalSeparator = lastComma > lastDot ? ',' : '.';
  } else {
    const separator = hasComma ? ',' : hasDot ? '.' : undefined;
    if (separator) {
      const groups = unsigned.split(separator);
      if (
        groups.length === 2 ||
        !groups.slice(1).every((group) => group.length === 3) ||
        groups[0].length > 3
      ) {
        decimalSeparator = separator;
      }
    }
  }

  let decimalIndex = decimalSeparator
    ? unsigned.lastIndexOf(decimalSeparator)
    : -1;
  if (decimalIndex >= 0 && decimalIndex === unsigned.length - 1) {
    decimalIndex = -1;
  }
  const integerPart =
    decimalIndex >= 0 ? unsigned.slice(0, decimalIndex) : unsigned;
  const fractionPart =
    decimalIndex >= 0 ? unsigned.slice(decimalIndex + 1) : '';
  if (
    (integerPart !== '' && !/^\d[\d.,]*$/.test(integerPart)) ||
    (integerPart === '' && decimalIndex < 0) ||
    (decimalIndex >= 0 && !/^\d+$/.test(fractionPart))
  ) {
    return null;
  }

  const normalized = `${sign}${integerPart.replace(/[.,]/g, '') || '0'}${
    decimalIndex >= 0 ? `.${fractionPart}` : ''
  }`;
  const parsed = Number(normalized);
  return Number.isFinite(parsed)
    ? { __excelNumber: true, value: parsed }
    : null;
};

export const isExcelNumberCell = (cell: unknown): cell is IExcelNumberCell =>
  typeof cell === 'object' &&
  cell !== null &&
  (cell as IExcelNumberCell).__excelNumber === true;

/**
 * Converte a marcação explícita em uma célula numérica HTML do Excel.
 */
const injectNumericFormats = (html: string): string =>
  html.replace(
    /(<td\b[^>]*?)\sdata-excel-number="([^"]+)"([^>]*>)/g,
    (_match, beforeAttribute, value, afterAttribute) => {
      let openTag = `${beforeAttribute}${afterAttribute}`;
      if (/style\s*=/.test(openTag)) {
        openTag = openTag.replace(
          /style\s*=\s*"/,
          'style="mso-number-format:General;'
        );
      } else {
        openTag = openTag.replace(/>$/, ' style="mso-number-format:General">');
      }

      return openTag.replace(/>$/, ` x:num="${value}">`);
    }
  );

export const createXMLTable = (
  tableContent: string,
  fileName: string
): string => {
  const processedContent = injectNumericFormats(tableContent);

  return `
    <html xmlns:x="urn:schemas-microsoft-com:office:excel">
      <head>
        <meta charset="UTF-8">
        <style>
          td { mso-number-format: "\\@"; }
        </style>
        <xml>
          <x:ExcelWorkbook>
            <x:ExcelWorksheets>
              <x:ExcelWorksheet>
                <x:Name>${fileName}</x:Name>
                <x:WorksheetOptions>
                  <x:Panes></x:Panes>
                </x:WorksheetOptions>
              </x:ExcelWorksheet>
            </x:ExcelWorksheets>
          </x:ExcelWorkbook>
        </xml>
      </head>
      <body>
        <table border='1' bordercolor='white'>
          ${processedContent}
        </table>
      </body>
    </html>
  `;
};
